import React, {FocusEvent, ReactNode, useRef} from 'react';

import {pushDataLayer} from '@optimaxdev/analytics/desktop/lib/pushDataLayer/index';
import {sendHashedEmailEvent} from '@optimaxdev/analytics/desktop/lib/sendHashedEmailEvent/index';

import md5 from 'blueimp-md5';

import {useFormContext} from 'react-hook-form';
import isEmail from 'validator/lib/isEmail';

import {DATALAYER_EMAIL} from 'constants/analytics';
import {GooglePlacesType} from 'desktop/components/addressField/addressField.types';
import {
    AddressFormItemType,
    AddressFormType,
    FormStructureItemType,
} from 'desktop/types/addressType';
import {useGetCountries, useGetRegions} from 'hooks/location/location';

import {sessionStore} from 'libs/storage/storage';

import {INITIAL_FORM_VALUES} from '../../constants/deliveryStep';
import {useDeliveryFieldRegister} from '../../hooks/useDeliveryFieldRegister/useDeliveryFieldRegister';
import {useMapAutocomplete} from '../../hooks/useMapAutocomplete/useMapAutocomplete';
import {useSaveDeliveryFieldInRedux} from '../../hooks/useSaveDeliveryFieldInRedux/useSaveDeliveryFieldInRedux';
import {DeliveryFormTooltip} from '../deliveryFormTooltip/deliveryFormTooltip';

export type PropsType = {
    fieldKey: keyof AddressFormItemType;
    field: FormStructureItemType;
    countryValue: string;
    target: 'shippingAddress' | 'billingAddress';
};

/**
 * Delivery form field for checkout
 * @param {PropsType} props - props component
 */
// eslint-disable-next-line max-statements
export const DeliveryFormField = ({countryValue, field, fieldKey, target}: PropsType) => {
    const optionalFieldRef = useRef<HTMLInputElement>(null);
    const {countries = []} = useGetCountries();
    const {regions: states = {}} = useGetRegions(countryValue);
    const {setValue} = useFormContext<AddressFormType>();

    const {
        field: {value, ...fieldProps},
        fieldState: {error, isTouched},
    } = useDeliveryFieldRegister(fieldKey, target);

    const mapAutocomplete = useMapAutocomplete(target);
    const saveFieldInRedux = useSaveDeliveryFieldInRedux({fieldKey, target});

    const hasPhoneUsageTip = fieldKey === 'phone' && !value;

    const sendEmailToDataLayer = (email: string) => {
        const isValidEmail = isEmail(email);
        if (!isValidEmail) return;
        const emailList: string[] = sessionStore.get(DATALAYER_EMAIL) || [];
        const isNewEmail = !emailList.find(emailItem => emailItem === email);
        if (!isNewEmail) return;
        sessionStore.set(DATALAYER_EMAIL, [...emailList, email]);
        const hashedEmail = md5(email.trim().toLowerCase());
        pushDataLayer({
            mktg_email: email,
            mktg_hashed_email: hashedEmail,
        });
    };

    const handleBlur = (e: FocusEvent<HTMLInputElement, Element>) => {
        fieldProps.onBlur();
        if (fieldKey === 'email') {
            sendEmailToDataLayer(e.target.value);
            sendHashedEmailEvent(e.target.value);
        }
    };

    const handleChange = (changedValue: ReactNode) => {
        if (fieldKey === 'country') {
            setValue(`${target}.regionID`, INITIAL_FORM_VALUES.regionID.value, {
                shouldTouch: false,
                shouldValidate: false,
            });
            setValue(`${target}.state`, INITIAL_FORM_VALUES.state.value, {
                shouldTouch: false,
                shouldValidate: false,
            });
        }
        fieldProps.onBlur();
        setValue(`${target}.${fieldKey}`, changedValue, {
            shouldDirty: true,
            shouldTouch: true,
            shouldValidate: true,
        });
        saveFieldInRedux();
    };

    const mapAutoCompleteToAddress = (places: GooglePlacesType) => {
        mapAutocomplete(places);
        optionalFieldRef?.current?.focus();
    };

    return typeof value === 'object' ? null : (
        <>
            <field.component
                {...fieldProps}
                success={
                    !error &&
                    // for state the default value is "0", this is why we need this special condition
                    (fieldKey === 'state' ? value !== INITIAL_FORM_VALUES.state.value : isTouched)
                }
                attributes={field.attributes}
                countries={countries}
                country={countryValue}
                error={Boolean(error?.message)}
                errorMessage={error?.message || ''}
                fieldRef={optionalFieldRef}
                label={field.label}
                onAutocomplete={mapAutoCompleteToAddress}
                onBlur={handleBlur}
                onChange={handleChange}
                states={states}
                target={target}
                value={value!}
            />

            {hasPhoneUsageTip && <DeliveryFormTooltip />}
        </>
    );
};
